import { useAutoneTranslation } from '@autone/translations';
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useMemo,
} from 'react';

import {
  TailwindCard as Card,
  TailwindCardContent as CardContent,
  TailwindCardTitle as CardTitle,
  cn,
  Progress,
  ProgressLegendItem,
  Separator,
} from '../../tailwind';

type GenericKPICardProps = HTMLAttributes<HTMLDivElement>;

export const GenericKPICard = forwardRef<HTMLDivElement, GenericKPICardProps>(
  ({ className, children, ...props }, ref) => (
    <Card
      className={cn('w-full max-w-lg shadow-md', className)}
      ref={ref}
      {...props}
    >
      {children}
    </Card>
  ),
);

type GenericKPICardTitleProps = {
  icon: ReactNode;
  title: string;
  headerTooltipIcon?: ReactNode;
} & GenericKPICardProps;

export const GenericKPICardTitle = forwardRef<
  HTMLDivElement,
  GenericKPICardTitleProps
>(({ icon, title, headerTooltipIcon, className, ...props }, ref) => (
  <CardTitle
    className={cn(
      'font-medium first-letter:uppercase flex items-center gap-2 p-4 justify-between',
      className,
    )}
    ref={ref}
    {...props}
  >
    <div className="flex items-center gap-3 text-nowrap">
      {icon} {title}
    </div>
    {headerTooltipIcon}
  </CardTitle>
));

export const GenericKPICardIconWrapper = forwardRef<
  HTMLDivElement,
  GenericKPICardProps
>(({ className, children, ...props }, ref) => (
  <div
    className={cn('bg-primary-10 p-2 rounded-full', className)}
    ref={ref}
    {...props}
  >
    {children}
  </div>
));

export const GenericKPICardContent = forwardRef<
  HTMLDivElement,
  GenericKPICardProps
>(({ className, children, ...props }, ref) => (
  <CardContent
    className={cn('flex flex-col gap-2 px-4 pb-4', className)}
    ref={ref}
    {...props}
  >
    {children}
  </CardContent>
));

type GenericKPICardProgressBarProps = {
  segments: { value: number | null; color: string }[];
  target: number | null;
} & GenericKPICardProps;

export const GenericKPICardProgressBar = forwardRef<
  HTMLDivElement,
  GenericKPICardProgressBarProps
>(({ segments, target, className, ...props }, ref) => {
  const filteredSegments = useMemo(
    () =>
      segments.filter((segment) => segment.value !== null && target !== null),
    [segments, target],
  );

  return (
    <Progress
      className={className}
      ref={ref}
      segments={filteredSegments.map((segment) => ({
        value: Number(((segment.value! / target!) * 100).toFixed(0)),
        color: segment.color,
      }))}
      {...props}
    />
  );
});

export const GenericKPICardMetricsWrapper = forwardRef<
  HTMLDivElement,
  GenericKPICardProps
>(({ className, children, ...props }, ref) => (
  <div
    className={cn(
      'flex justify-between [&>*:first-child]:text-left text-right',
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
  </div>
));

type GenericKPICardMetricsProps = {
  value?: string | number | null;
  label: string;
  alignRight?: boolean;
  icon?: ReactNode;
} & GenericKPICardProps;

export const GenericKPICardMetrics = forwardRef<
  HTMLDivElement,
  GenericKPICardMetricsProps
>(({ className, value, label, icon, alignRight, ...props }, ref) => {
  const { common } = useAutoneTranslation();
  return (
    <div
      className={cn(
        'flex flex-col leading-none w-full gap-1 text-sm',
        alignRight ? 'items-end' : '',
        className,
      )}
      ref={ref}
      {...props}
    >
      <div className="flex items-center gap-1">
        <p className="text-xl font-medium">{value ?? common('na')}</p>
        {icon}
      </div>
      <p className="text-grey-80 max-w-full leading-none first-letter:uppercase">
        {label}
      </p>
    </div>
  );
});

type GenericKPICardLegendItemProps = {
  label: string;
  value: ReactNode;
} & GenericKPICardProps;

export const GenericKPICardProgressBarLegendItem = ({
  className,
  label,
  value,
  color,
}: GenericKPICardLegendItemProps) => (
  <ProgressLegendItem
    label={label}
    value={value}
    color={color}
    className={cn('text-xs', className)}
  />
);

type GenericKPIDoubleCardWrapperProps = {
  leftBody: ReactNode;
  rightBody: ReactNode;
} & GenericKPICardProps;

export const GenericKPIDoubleCardWrapper = forwardRef<
  HTMLDivElement,
  GenericKPIDoubleCardWrapperProps
>(({ className, leftBody, rightBody, ...props }, ref) => (
  <GenericKPICard
    className={cn(
      'flex flex-row justify-between py-6 min-w-[700px]',
      className,
    )}
    ref={ref}
    {...props}
  >
    <div className="w-1/2">{leftBody}</div>
    <Separator orientation="vertical" />
    <div className="w-1/2">{rightBody}</div>
  </GenericKPICard>
));

type GenericKPIProgressBarRowWrapperProps = {
  fieldName: string;
  value: string;
  progressBar: ReactNode;
} & GenericKPICardProps;

export const GenericKPIProgressBarRowWrapper = forwardRef<
  HTMLDivElement,
  GenericKPIProgressBarRowWrapperProps
>(({ className, fieldName, value, progressBar, ...props }, ref) => (
  <div className={cn('flex items-center', className)} ref={ref} {...props}>
    <div className="w-48 mr-2 text-grey-80">{fieldName}</div>
    <div className="w-full">{progressBar}</div>
    {value && <div className="min-w-8 text-right font-semibold">{value}</div>}
  </div>
));
